import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import KeyIcon from "@mui/icons-material/VpnKey";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import NewUser from "./EnterpriseUsers/NewUser";
import GenerateTempKey from "./EnterpriseUsers/GenerateTemporaryKey";
import EditDeleteUsers from "./EnterpriseUsers/EditDeleteUsers";
import { useAuth } from "../../context/AuthContext";
import { useDashboardTheme } from "../../context/DashboardThemeContext";
import { useAlert } from "../../context/AlertContext";

const ENDPOINTS = {
  listUsers: "/api/auth/users/",
  createUser: "/api/auth/users/create/",
  editUser: "/api/auth/users/edit/",
  deleteUser: "/api/auth/users/delete/",
  regenerateKey: "/api/auth/users/regenerate-temporary-key/",
  sendKeyEmail: "/api/auth/users/send-temporary-key-email/",
  limits: "/api/enterprise/empresa/limits/",
  updateLimits: "/api/enterprise/empresa/update-user-limit/",
};

const getUserRole = (item) => item?.role || item?.rol || "";
const getUserName = (item) => item?.name || item?.nombre || "";
const getUserEmail = (item) => item?.email || item?.correo || "";
const getUserPhone = (item) => item?.phone || item?.telefono || "";
const getUserActive = (item) => {
  if (typeof item?.is_active === "boolean") return item.is_active;
  if (typeof item?.activo === "boolean") return item.activo;
  return true;
};

const EnterpriseUsers = ({
  canSeeSuperUsers = false,
  canCreateSuperUsers = false,
}) => {
  const { accessToken, user, logout } = useAuth();
  const { dashboardTheme } = useDashboardTheme();
  const alert = useAlert();

  const [openNewUser, setOpenNewUser] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [openGenerate, setOpenGenerate] = useState(false);
  const [openEditDelete, setOpenEditDelete] = useState(false);
  const [editDeleteMode, setEditDeleteMode] = useState("edit");
  const [limits, setLimits] = useState(null);
  const [newUserFormKey, setNewUserFormKey] = useState(0);
  const [editingConfig, setEditingConfig] = useState(false);
  const [limitInput, setLimitInput] = useState("");
  const [loginUrlInput, setLoginUrlInput] = useState("");
  const [savingConfig, setSavingConfig] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [loading, setLoading] = useState(true);

  const isSuper = user?.role === "super_admin";
  const permissions = user?.permissions || [];
  const canView = isSuper || permissions.includes("enterprise.view");
  const canCreate = isSuper || permissions.includes("enterprise.create");
  const canEdit = isSuper || permissions.includes("enterprise.edit");
  const canDelete = isSuper || permissions.includes("enterprise.delete");
  const canManageConfig = isSuper;

  const notify = useCallback(
    (message, severity = "success") => {
      if (typeof alert?.showAlert === "function") {
        alert.showAlert(message, severity);
      }
    },
    [alert]
  );

  const getToken = useCallback(() => {
    return accessToken || localStorage.getItem("accessToken") || "";
  }, [accessToken]);

  const parseJsonSafe = async (res) => {
    try {
      return await res.json();
    } catch {
      return { ok: false, error: "Respuesta inválida del servidor" };
    }
  };

  const authFetch = useCallback(
    async (url, options = {}) => {
      const token = getToken();

      const headers = {
        Accept: "application/json",
        ...(options.headers || {}),
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      return fetch(url, {
        ...options,
        headers,
      });
    },
    [getToken]
  );

  const usuariosVisibles = useMemo(() => {
    if (canSeeSuperUsers) return usuarios;

    return usuarios.filter((item) => getUserRole(item) !== "super_admin");
  }, [usuarios, canSeeSuperUsers]);

  const usuariosOperativos = useMemo(() => {
    return usuarios.filter((item) => getUserRole(item) !== "super_admin");
  }, [usuarios]);

  const rawLimit = Number(limits?.limite_usuarios ?? 0);
  const limiteUsuarios = Math.max(rawLimit - 1, 0);
  const usuariosActuales = usuariosOperativos.length;
  const loginUrl = limits?.login_url || "";

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: dashboardTheme.card_background,
      color: dashboardTheme.text_color,
      "& fieldset": {
        borderColor: dashboardTheme.border_color,
      },
      "&:hover fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
      "&.Mui-focused fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
    },
    "& .MuiInputLabel-root": {
      color: dashboardTheme.muted_text_color,
      "&.Mui-focused": {
        color: dashboardTheme.primary_color,
      },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: dashboardTheme.muted_text_color,
    },
    "& .MuiSvgIcon-root": {
      color: dashboardTheme.muted_text_color,
    },
  };

  const primaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    bgcolor: dashboardTheme.button_color,
    color: dashboardTheme.button_text_color,
    boxShadow: "none",
    "&:hover": {
      bgcolor: dashboardTheme.button_hover_color || dashboardTheme.button_color,
      boxShadow: "none",
    },
  };

  const iconButtonSx = {
    bgcolor:
      dashboardTheme.theme_mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : `${dashboardTheme.primary_color}12`,
    "&:hover": {
      bgcolor:
        dashboardTheme.theme_mode === "dark"
          ? "rgba(255,255,255,0.1)"
          : `${dashboardTheme.primary_color}20`,
    },
  };

  const usuariosAdornment = (
    <InputAdornment
      position="end"
      sx={{
        color: dashboardTheme.theme_mode === "dark" ? "#FFFFFF" : dashboardTheme.muted_text_color,
        "& .MuiTypography-root": {
          color: dashboardTheme.theme_mode === "dark" ? "#FFFFFF" : dashboardTheme.muted_text_color,
        },
      }}
    >
      usuarios
    </InputAdornment>
  );

  const cargarUsuarios = useCallback(async () => {
    if (!canView) return;

    try {
      const res = await authFetch(ENDPOINTS.listUsers);
      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      const list = data.usuarios || data.users || data.results || [];

      setUsuarios(Array.isArray(list) ? list : []);
    } catch {
      setUsuarios([]);
      notify("No se pudo cargar la lista de usuarios.", "error");
    }
  }, [authFetch, canView, logout, notify]);

  const cargarLimites = useCallback(async () => {
    if (!canView) return;

    try {
      const res = await authFetch(ENDPOINTS.limits);
      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (res.ok && data.ok) {
        setLimits(data);
        setLimitInput(String(data.limite_usuarios ?? ""));
        setLoginUrlInput(data.login_url || "");
      } else {
        setLimits(null);
        setLimitInput("");
        setLoginUrlInput("");
      }
    } catch {
      setLimits(null);
      setLimitInput("");
      setLoginUrlInput("");
      notify("No se pudo cargar la configuración de usuarios.", "error");
    }
  }, [authFetch, canView, logout, notify]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([cargarUsuarios(), cargarLimites()]);
    setLoading(false);
  }, [cargarUsuarios, cargarLimites]);

  useEffect(() => {
    if (!getToken() || !canView) {
      setLoading(false);
      return;
    }

    loadAll();
  }, [getToken, canView, loadAll]);

  const cerrarMenu = () => {
    if (sendingEmail) return;
    setAnchorEl(null);
    setUsuarioSeleccionado(null);
  };

  const abrirMenuEnvio = (event, selectedUser) => {
    if (sendingEmail) return;
    setAnchorEl(event.currentTarget);
    setUsuarioSeleccionado(selectedUser);
  };

  const abrirNuevoUsuario = () => {
    if (!canCreate) {
      notify("No tienes permisos para crear usuarios.", "error");
      return;
    }

    if (usuariosActuales >= limiteUsuarios) {
      notify("Se alcanzó el límite máximo de usuarios permitidos.", "error");
      return;
    }

    if (!loginUrl) {
      notify("Configura primero la URL de login para enviar invitaciones.", "error");
      return;
    }

    setOpenNewUser(true);
  };

  const cerrarNuevoUsuario = () => {
    setOpenNewUser(false);
    setNewUserFormKey((prev) => prev + 1);
  };

  const enviarWhatsApp = (selectedUser) => {
    if (!loginUrl) {
      notify("Configura primero la URL de login.", "error");
      return;
    }

    const numero = String(getUserPhone(selectedUser) || "").replace(/\D/g, "");
    const numeroFinal = numero.startsWith("593") ? numero : `593${numero}`;

    const mensaje = encodeURIComponent(
      `Hola ${getUserName(selectedUser)},\n\nTe han creado una cuenta de acceso en ${limits?.empresa_nombre || "la empresa"}.\n\nCorreo: ${getUserEmail(selectedUser)}\nClave temporal: ${selectedUser?.clave_temporal || ""}\n\nPuedes ingresar aquí:\n${loginUrl}`
    );

    window.open(`https://wa.me/${numeroFinal}?text=${mensaje}`, "_blank");
    notify("Clave enviada por WhatsApp", "info");
    cerrarMenu();
  };

  const enviarCorreo = async (selectedUser) => {
    if (!selectedUser || sendingEmail) return;

    if (!loginUrl) {
      notify("Configura primero la URL de login.", "error");
      return;
    }

    setSendingEmail(true);
    setAnchorEl(null);

    try {
      const res = await authFetch(ENDPOINTS.sendKeyEmail, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: selectedUser.id,
          login_url: loginUrl,
        }),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (res.ok && data.ok) {
        notify(data?.msg || data?.message || "Clave enviada por correo", "success");
      } else {
        notify(data?.error || "Error al enviar correo", "error");
      }
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setSendingEmail(false);
      setUsuarioSeleccionado(null);
    }
  };

  const mostrarRol = (role) => {
    if (role === "super_admin") return "Super Admin";
    if (role === "admin") return "Administrador";
    if (role === "staff") return "Staff";
    return "Usuario";
  };

  const abrirGenerarClave = (selectedUser) => {
    if (!canEdit) {
      notify("No tienes permisos para generar claves.", "error");
      return;
    }

    setUsuarioSeleccionado(selectedUser);
    setOpenGenerate(true);
  };

  const abrirEditar = (selectedUser) => {
    if (!canEdit) {
      notify("No tienes permisos para editar usuarios.", "error");
      return;
    }

    setUsuarioSeleccionado(selectedUser);
    setEditDeleteMode("edit");
    setOpenEditDelete(true);
  };

  const abrirEliminar = (selectedUser) => {
    if (!canDelete) {
      notify("No tienes permisos para eliminar usuarios.", "error");
      return;
    }

    setUsuarioSeleccionado(selectedUser);
    setEditDeleteMode("delete");
    setOpenEditDelete(true);
  };

  const guardarConfiguracionUsuarios = async () => {
    if (!canManageConfig) return;

    const valor = String(limitInput || "").trim();
    const nextLoginUrl = String(loginUrlInput || "").trim();

    if (!valor) {
      notify("Ingresa un límite de usuarios válido.", "error");
      return;
    }

    if (!nextLoginUrl) {
      notify("Ingresa la URL de login para las invitaciones.", "error");
      return;
    }

    try {
      setSavingConfig(true);

      const res = await authFetch(ENDPOINTS.updateLimits, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          limite_usuarios: valor,
          limite_documentos: limits?.limite_documentos ?? "",
          login_url: nextLoginUrl,
        }),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (!res.ok || !data.ok) {
        notify(data?.error || "No se pudo actualizar la configuración.", "error");
        return;
      }

      setLimits((prev) => ({
        ...(prev || {}),
        ...data,
      }));

      setLimitInput(String(data.limite_usuarios ?? ""));
      setLoginUrlInput(data.login_url || "");
      setEditingConfig(false);
      notify(data?.message || "Configuración actualizada correctamente.", "success");
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setSavingConfig(false);
    }
  };

  if (!canView) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: "14px",
          border: `1px solid ${dashboardTheme.border_color}`,
          bgcolor: dashboardTheme.card_background,
        }}
      >
        <Stack spacing={0.7}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: dashboardTheme.text_color,
            }}
          >
            Sin permisos
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: dashboardTheme.muted_text_color,
              lineHeight: 1.6,
            }}
          >
            No tienes permisos para ver la administración de usuarios.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography
        sx={{
          fontSize: { xs: 18, md: 20 },
          fontWeight: 600,
          color: dashboardTheme.text_color,
          mb: 2,
        }}
      >
        Gestión de Usuarios del Sistema
      </Typography>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: "14px",
            p: 2,
            border: `1px solid ${dashboardTheme.border_color}`,
            bgcolor: dashboardTheme.card_background,
            boxShadow:
              dashboardTheme.theme_mode === "dark"
                ? "none"
                : "0 4px 10px rgba(0,0,0,0.08)",
          }}
        >
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 2,
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor:
                      dashboardTheme.theme_mode === "dark"
                        ? "rgba(255,255,255,0.06)"
                        : `${dashboardTheme.primary_color}14`,
                  }}
                >
                  <GroupsRoundedIcon sx={{ color: dashboardTheme.primary_color }} />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: dashboardTheme.muted_text_color,
                      fontWeight: 600,
                    }}
                  >
                    Usuarios permitidos
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 16,
                      color: dashboardTheme.primary_color,
                      fontWeight: 700,
                    }}
                  >
                    {usuariosActuales} / {limiteUsuarios}
                  </Typography>
                </Box>
              </Box>

              {canManageConfig && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ width: { xs: "100%", md: "auto" } }}
                >
                  {!editingConfig ? (
                    <Button
                      variant="contained"
                      startIcon={<EditRoundedIcon />}
                      onClick={() => {
                        setLimitInput(String(rawLimit || ""));
                        setLoginUrlInput(loginUrl || "");
                        setEditingConfig(true);
                      }}
                      sx={primaryButtonSx}
                    >
                      Editar configuración
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<SaveRoundedIcon />}
                      onClick={guardarConfiguracionUsuarios}
                      disabled={savingConfig}
                      sx={primaryButtonSx}
                    >
                      {savingConfig ? "Guardando..." : "Guardar configuración"}
                    </Button>
                  )}
                </Stack>
              )}
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField
                size="small"
                label="Capacidad"
                value={editingConfig ? limitInput : rawLimit}
                onChange={(event) => setLimitInput(event.target.value.replace(/\D/g, ""))}
                disabled={!editingConfig || savingConfig}
                sx={{ ...fieldSx, width: { xs: "100%", md: 180 } }}
                InputProps={{
                  endAdornment: usuariosAdornment,
                }}
              />

              <TextField
                size="small"
                label="URL de login para invitaciones"
                value={editingConfig ? loginUrlInput : loginUrl}
                onChange={(event) => setLoginUrlInput(event.target.value)}
                disabled={!editingConfig || savingConfig}
                fullWidth
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Stack>
        </Paper>
      </Stack>

      <Button
        variant="contained"
        onClick={abrirNuevoUsuario}
        disabled={loading || !canCreate}
        sx={{
          ...primaryButtonSx,
          mb: 3,
        }}
      >
        Crear nuevo usuario
      </Button>

      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: "12px",
          border: `1px solid ${dashboardTheme.border_color}`,
          bgcolor: dashboardTheme.card_background,
        }}
      >
        {loading ? (
          <Stack
            spacing={1.2}
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: 220 }}
          >
            <CircularProgress size={30} sx={{ color: dashboardTheme.primary_color }} />
            <Typography sx={{ fontSize: 14, color: dashboardTheme.muted_text_color }}>
              Cargando usuarios...
            </Typography>
          </Stack>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: dashboardTheme.table_header_color }}>
                <TableRow>
                  <TableCell sx={{ color: dashboardTheme.table_header_text_color, fontWeight: 700 }}>
                    Nombre
                  </TableCell>
                  <TableCell sx={{ color: dashboardTheme.table_header_text_color, fontWeight: 700 }}>
                    Correo
                  </TableCell>
                  <TableCell sx={{ color: dashboardTheme.table_header_text_color, fontWeight: 700 }}>
                    Teléfono
                  </TableCell>
                  <TableCell sx={{ color: dashboardTheme.table_header_text_color, fontWeight: 700 }}>
                    Rol
                  </TableCell>
                  <TableCell sx={{ color: dashboardTheme.table_header_text_color, fontWeight: 700 }}>
                    Estado
                  </TableCell>
                  <TableCell sx={{ color: dashboardTheme.table_header_text_color, fontWeight: 700 }}>
                    Clave Temporal
                  </TableCell>
                  <TableCell sx={{ color: dashboardTheme.table_header_text_color, fontWeight: 700 }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {usuariosVisibles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography
                        sx={{
                          py: 3,
                          textAlign: "center",
                          color: dashboardTheme.muted_text_color,
                        }}
                      >
                        No hay usuarios registrados.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  usuariosVisibles.map((item) => {
                    const active = getUserActive(item);
                    const role = getUserRole(item);

                    return (
                      <TableRow
                        key={item.id}
                        sx={{
                          bgcolor: dashboardTheme.table_background,
                          "& td": {
                            color: dashboardTheme.text_color,
                            borderColor: dashboardTheme.border_color,
                          },
                        }}
                      >
                        <TableCell>{getUserName(item)}</TableCell>
                        <TableCell>{getUserEmail(item)}</TableCell>
                        <TableCell>{getUserPhone(item)}</TableCell>
                        <TableCell>{mostrarRol(role)}</TableCell>

                        <TableCell>
                          <Chip
                            label={active ? "Activo" : "Inactivo"}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: active
                                ? dashboardTheme.theme_mode === "dark"
                                  ? "rgba(34,197,94,0.14)"
                                  : "#dcfce7"
                                : dashboardTheme.theme_mode === "dark"
                                ? "rgba(239,68,68,0.14)"
                                : "#fee2e2",
                              color: active ? "#16a34a" : "#dc2626",
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          {item.clave_temporal ? (
                            <Chip
                              label="Pendiente"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor:
                                  dashboardTheme.theme_mode === "dark"
                                    ? "rgba(245,158,11,0.14)"
                                    : "#fef3c7",
                                color: "#d97706",
                              }}
                            />
                          ) : (
                            <Chip
                              label="Actualizada"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor:
                                  dashboardTheme.theme_mode === "dark"
                                    ? "rgba(255,255,255,0.08)"
                                    : "#f3f4f6",
                                color: dashboardTheme.muted_text_color,
                              }}
                            />
                          )}
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={0.7}>
                            {item.clave_temporal ? (
                              <Tooltip title="Enviar clave temporal">
                                <span>
                                  <IconButton
                                    onClick={(event) => abrirMenuEnvio(event, item)}
                                    sx={iconButtonSx}
                                    disabled={sendingEmail}
                                    size="small"
                                  >
                                    <SendIcon sx={{ color: dashboardTheme.primary_color }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Generar nueva clave temporal">
                                <span>
                                  <IconButton
                                    onClick={() => abrirGenerarClave(item)}
                                    sx={iconButtonSx}
                                    disabled={!canEdit}
                                    size="small"
                                  >
                                    <KeyIcon sx={{ color: "#9333ea" }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            <Tooltip title="Editar usuario">
                              <span>
                                <IconButton
                                  onClick={() => abrirEditar(item)}
                                  sx={iconButtonSx}
                                  disabled={!canEdit}
                                  size="small"
                                >
                                  <EditIcon sx={{ color: dashboardTheme.primary_color }} />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Eliminar usuario">
                              <span>
                                <IconButton
                                  onClick={() => abrirEliminar(item)}
                                  disabled={!canDelete}
                                  size="small"
                                  sx={{
                                    bgcolor:
                                      dashboardTheme.theme_mode === "dark"
                                        ? "rgba(239,68,68,0.1)"
                                        : "#fee2e2",
                                    "&:hover": {
                                      bgcolor:
                                        dashboardTheme.theme_mode === "dark"
                                          ? "rgba(239,68,68,0.16)"
                                          : "#fecaca",
                                    },
                                  }}
                                >
                                  <DeleteIcon sx={{ color: "#dc2626" }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={cerrarMenu}
        PaperProps={{
          sx: {
            bgcolor: dashboardTheme.card_background,
            color: dashboardTheme.text_color,
            border: `1px solid ${dashboardTheme.border_color}`,
          },
        }}
      >
        <MenuItem onClick={() => enviarWhatsApp(usuarioSeleccionado)}>
          <WhatsAppIcon sx={{ color: "green", mr: 1 }} /> WhatsApp
        </MenuItem>

        <MenuItem onClick={() => enviarCorreo(usuarioSeleccionado)} disabled={sendingEmail}>
          <EmailIcon sx={{ color: dashboardTheme.primary_color, mr: 1 }} /> Correo
        </MenuItem>
      </Menu>

      {openNewUser && (
        <NewUser
          key={newUserFormKey}
          open={openNewUser}
          onClose={cerrarNuevoUsuario}
          onCreated={async () => {
            await loadAll();
            cerrarNuevoUsuario();
            notify("Usuario creado correctamente", "success");
          }}
          canCreateSuperUsers={false}
          loginUrl={loginUrl}
          createEndpoint={ENDPOINTS.createUser}
        />
      )}

      <GenerateTempKey
        open={openGenerate}
        user={usuarioSeleccionado}
        empresaNombre={limits?.empresa_nombre || "la empresa"}
        loginUrl={loginUrl}
        regenerateEndpoint={ENDPOINTS.regenerateKey}
        sendEmailEndpoint={ENDPOINTS.sendKeyEmail}
        onClose={() => setOpenGenerate(false)}
        onDone={async (message, severity = "success") => {
          await cargarUsuarios();
          notify(message || "Clave temporal generada", severity);
        }}
      />

      <EditDeleteUsers
        open={openEditDelete}
        mode={editDeleteMode}
        user={usuarioSeleccionado}
        editEndpoint={ENDPOINTS.editUser}
        deleteEndpoint={ENDPOINTS.deleteUser}
        onClose={() => setOpenEditDelete(false)}
        onDone={async (message, severity = "success") => {
          await loadAll();
          notify(message || "Actualizado", severity);
        }}
      />

      <Dialog
        open={sendingEmail}
        PaperProps={{
          sx: {
            borderRadius: "18px",
            px: 2,
            py: 1.5,
            minWidth: 280,
            bgcolor: dashboardTheme.card_background,
            border: `1px solid ${dashboardTheme.border_color}`,
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            textAlign: "center",
            py: 3,
          }}
        >
          <CircularProgress size={34} sx={{ color: dashboardTheme.primary_color }} />

          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                color: dashboardTheme.primary_color,
                mb: 0.5,
              }}
            >
              Enviando clave temporal
            </Typography>

            <Typography
              sx={{
                color: dashboardTheme.muted_text_color,
                fontSize: 14,
              }}
            >
              Por favor espera un momento...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EnterpriseUsers;